import React, { useEffect, useState } from 'react';
import { Card, CardBody, Container } from "reactstrap";
import Breadcrumb from "Components/UI/Breadcrumb";
import { getBookingById } from 'helpers/API Calls/Operations/booking';
import Spinner from 'Components/UI/Spinner';
import convertDateFormat from 'Components/Date/DateFormat';
import formatCurrency from 'Components/helpers/CurrencyFormat';
import { useParams } from 'react-router-dom';
import RefundPayment from './RefundPayment';

interface Product {
  id: number;
  product_id: number;
  item_price: number;
  quantity: number;
  product_name: string;
}

function InvoiceDetail() {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<any>(null);
  const [refundModal, setRefundModal] = useState<boolean>(false);
  
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const response = await getBookingById(Number(id));
      if (response.success) {
        setData(response.data);
      }
      setIsLoading(false);
    }
    fetchData();
  }, [id]);

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumb items={[{ label: "Invoices" }]} />
          <div className="position-relative vh-100"><Spinner/></div>
        </Container>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumb items={[{ label: "Invoices" }]} />
          <Card>
            <CardBody>No invoice data found.</CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  let subtotal = data.products.reduce((acc: number, product: Product) => 
    acc + (product.item_price * product.quantity), 0
  );

  subtotal += data.packages.reduce((acc: number, pkg: any) => 
    acc + (pkg.item_price * pkg.quantity), 0
  );

  const toBePaid = data.payments[0].payment_status_slug === 'paid' ?
  Number(data.payments[0].total_amount) - Number(data.payments[0].paid_amount) || 0 : 0;
  const isRefundable = data.payments[0].payment_method === 'stripe' 
    && (data.payments[0].payment_status_slug === 'paid' || data.payments[0].payment_status_slug === 'deposit_collected');
  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Invoices", link: "/payments" }, {label: 'Details'}]} />
        <Card>
          <CardBody>
            <div className="bg-white p-4 rounded">

              {/* Invoice Header */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <h4>Business Information</h4>
                  <p><strong>Castle Hire UK</strong></p>
                  <p>123 Castle Street</p>
                  <p>London, UK</p>
                  <p>Tel: +44 20 1234 5678</p>
                  <p>Email: info@castlehire.co.uk</p>
                </div>
                <div className="col-md-6 text-md-end">
                  <h4>Invoice Details</h4>
                  <p><strong>Invoice Number:</strong> {data.payments[0].invoice_id}</p>
                  <p><strong>Booking Date:</strong> {convertDateFormat(data.booking_date)}</p>
                  <p><strong>Booking ID:</strong> {data.id}</p>
                  <p><strong>Payment Status:</strong> {data.payments[0].payment_status}</p>
                </div>
              </div>

              {/* Customer Information */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <h4>Customer Details</h4>
                  <p><strong>Name:</strong> {data.customer.name}</p>
                  <p><strong>Email:</strong> {data.customer.email}</p>
                  <p><strong>Phone:</strong> {data.customer.phone_no_mobile}</p>
                  <p><strong>Address:</strong></p>
                  <p>{data.customer.address_line_1}</p>
                  <p>{data.customer.address_line_2}</p>
                  <p>{data.customer.town}</p>
                  <p>{data.customer.complete_postcode}</p>
                </div>
                {/* Delivery Information */}
                <div className="col-md-6 text-md-end">
                    <h4>Delivery Details</h4>
                    <div >
                        <p><strong>Venue:</strong> {data.delivery.venue}</p>
                        <p><strong>Address:</strong></p>
                        <p>{data.delivery.address_line_1}</p>
                        <p>{data.delivery.address_line_2}</p>
                        <p>{data.delivery.town}</p>
                        <p>{data.delivery.complete_postcode}</p>
                   </div>
                </div>
                
              </div>


              <div className="mb-4">
                <h4>Booking Notes</h4>
                <p>{data.booking_notes || 'No additional notes'}</p>
                <h4 className="mt-3">Operator Notes</h4>
                <p>{data.operator_notes || 'No Operator notes'}</p>
            </div>

              {/* Product Details */}
              <div className="mb-4">
                <h4>Rental Details</h4>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Quantity</th>
                        <th>Price per Item</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.products.map((product: Product) => (
                        <tr key={product.id}>
                          <td>{product?.product_name}</td>
                          <td>{product?.quantity}</td>
                          <td>{formatCurrency(product?.item_price?.toString())}</td>
                          <td>{formatCurrency((product?.item_price * product?.quantity)?.toString())}</td>
                        </tr>
                      ))}
                      {data.packages.map((pkg) => (
                        <tr key={pkg.id}>
                          <td>{pkg.package_name}</td>
                          <td>{pkg.quantity}</td>
                          <td>{formatCurrency(pkg.item_price.toString())}</td>
                          <td>{formatCurrency((pkg.item_price * pkg.quantity).toString())}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Payment Summary */}
              <div className="mb-4">
                <h4>Payment Summary</h4>
                <div className="border-top pt-3">
                  <div className="d-flex justify-content-between">
                    <span>Products Total:</span>
                    <span>{formatCurrency(subtotal.toString())}</span>
                  </div>
                  
                  <div className="d-flex justify-content-between">
                    <span>Cleaning Deposit:</span>
                    <span>{formatCurrency(data.payments[0].refundable_cleaning_deposit)}</span>
                  </div>
                  {data.accessibility_options && data.accessibility_options.length > 0 && (
                    <div className="d-flex justify-content-between">
                      <span>Additional Charges:</span>
                      <span>{formatCurrency(data.accessibility_options.reduce((acc: number, option: any) => 
                        acc + parseFloat(option.charges), 0).toString())}</span>
                    </div>
                  )}
                  {data?.payments?.[0]?.postcode_charges > 0 && (
                    <div className="d-flex justify-content-between">
                      <span>Delivery Charges:</span>
                      <span>{formatCurrency(data?.payments?.[0]?.postcode_charges)}</span>
                    </div>
                  )}

                  {parseFloat(data.payments[0].discount_amount) > 0 && (
                    <div className="d-flex justify-content-between">
                      <span>Discount:</span>
                      <span>-{formatCurrency(data.payments[0].discount_amount)}</span>
                    </div>
                  )}

                   <div className="d-flex justify-content-between">
                    <span>VAT ({data.payments[0].vat_percent}%):</span>
                    <span>{formatCurrency(data.payments[0].vat)}</span>
                  </div>
                  <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                    <strong>Total Amount:</strong>
                    <strong>{formatCurrency(data.payments[0].total_amount)}</strong>
                  </div>

                  {(data?.payments?.[0]?.deposit_collected === 1 || !!data?.payments?.[0]?.deposit_collected)  && 
                  <div className="d-flex justify-content-between">
                    <span>Deposit Collected:</span>
                    <span>- {formatCurrency(data.payments[0].deposit)}</span>
                  </div>}

                  {(data?.payments?.[0]?.deposit_collected === 1 || !!data?.payments?.[0]?.deposit_collected)  && 
                  <div className="d-flex justify-content-between">
                    <span>Remaining Amount:</span>
                    <span>{formatCurrency((data.payments[0].total_amount - data.payments[0].deposit).toString())}</span>
                  </div>}

                  {data.payments[0].payment_method === 'stripe' &&
                  <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                    <strong>Amount Paid:</strong>
                    <strong>{formatCurrency(data.payments[0].paid_amount)}</strong>
                  </div>}
                  
                  {data.payments[0].payment_method === 'stripe' && toBePaid > 0 && (
                    <div className="d-flex justify-content-between">
                      <strong>Remaining Balance:</strong>
                      <strong>{formatCurrency(toBePaid.toString())}</strong>
                    </div>
                  )}
                  {data.payments[0].payment_method === 'stripe' && toBePaid < 0 && (
                    <div className="d-flex justify-content-between">
                      <strong>Credit Balance:</strong>
                      <strong>{formatCurrency(Math.abs(toBePaid).toString())}</strong>
                    </div>
                  ) }
                  {/* : (
                    <div className="d-flex justify-content-between">
                      <strong>Fully Paid:</strong>
                      <strong>{formatCurrency(toBePaid.toString())}</strong>
                    </div>
                  )} */}


                  {data.payments[0].refund_amount > 0 && <div className="d-flex justify-content-between">
                    <strong>Amount Refunded:</strong>
                    <strong>{formatCurrency(data.payments[0].refund_amount)}</strong>
                  </div>}

                </div>
              </div>

              <div className="mb-4 d-md-flex align-items-center justify-content-between">
                <div>
                  <h4>Payment Information</h4>
                  <p><strong>Payment Method:</strong> {data.payments[0].payment_method.toUpperCase()}</p>
                  {data.payments[0].payment_reference && (
                    <p><strong>Payment Reference:</strong> {data.payments[0].payment_reference}</p>
                  )}
                </div>
                 <div>
                  {isRefundable && (
                    <button 
                      className='btn btn-warning my-2'
                      type='button' 
                      onClick={() => setRefundModal(true)}
                    >
                      <i className="mdi mdi-cash-refund me-1"></i>
                      Refund Deposit
                    </button>
                  )}
                 </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </Container>
       {refundModal && (
        <RefundPayment onCloseClick={() => setRefundModal(false)} data={data} />
      )}
    </div>
  )
}

export default InvoiceDetail;
